import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { createSearchParams, useNavigate } from 'react-router';
import { IConnectedPage } from '../../../../types/api/connectedPages/IConnectedPage';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { defaultDimensions } from './DefaultDimensions';
import { ConnectedPagesGraphBreadCrumb } from './partials/ConnectedPagesGraphBreadCrumb';
import { useConnectedPagesGraphResizeHandler } from './utils/useConnectedPagesGraphResizeHandler';
import { ConnectedPagesGraphContainer } from './partials/ConnectedPagesGraphContainer';
import { ConnectedPagesGraphCurrentItem } from './partials/ConnectedPagesGraphCurrentItem';
import { IDimensions } from './interfaces/IConnectedPagesGraphProperties';
import { LoadingSpinner } from '../../../../components/atoms/LoadingSpinner';
import { isNullOrUndefined } from '../../../../lib/typeGuards';
import { HeadingHierarchy } from '../../Heading/Heading';

enum PaginationDirection {
  NEXT = 'next',
  PREVIOUS = 'previous',
}

type ConnectedPagesGraphProperties = {
  connectedPages: IConnectedPage[];
  headlineLevel: HeadingHierarchy;
  dimensions?: IDimensions;
};

/**
 * Extracts a page object by given Hash
 */
const getPageByUrl = (url: string, page: IConnectedPage): IConnectedPage | null => {
  if (page?.pageLink.url.endsWith(url)) {
    return page;
  }

  if (page?.childPages && page?.childPages?.length > 0) {
    for (let i = 0; i < page?.childPages?.length; i += 1) {
      const childPage = getPageByUrl(url, page.childPages[i]);

      if (childPage) {
        return childPage;
      }
    }
  }

  return null;
};

type GraphState = {
  activeCustomActionButton?: string;
  currentSet: number;
  currentPage: IConnectedPage;
};

function getGraphStateFromUrl(params: string, connectedPages: IConnectedPage[]): GraphState {
  const searchParams = createSearchParams(params);

  const currentPageUrl = searchParams.get('currentPage');
  const currentSet = Number(searchParams.get('currentSet'));
  const currentPage = currentPageUrl ? getPageByUrl(currentPageUrl, connectedPages[0]) : connectedPages[0];

  return { activeCustomActionButton: undefined, currentSet, currentPage: currentPage || connectedPages[0] };
}
/**
 * Renders a visual representation for connected Pages, the Bubble Graph.
 *
 * State Flow is as follows:
 *
 * Initialization:
 * - Init the Graph from url if we have any search params there
 * - if no search params given -> use default from backend in json
 *
 *  While switching:
 * - switching to different views ( e.g clicking a bubble) will navigate the url.
 * - the navigation will change the location.search param ( must not trigger SPA fetch route ! )
 * - this will trigger a useEffect change, where we collect the params from the address bar and keep state in sync.
 *
 * We opted to use this state flow, instead of changing the state on click and updating the url after this because.
 * - does not break browser back/forward button as component is updated with url
 * - no rendering of component when switching to pages where no params are given
 * - deeplinking is possible via SSR, when initalizing with search params
 */
export function ConnectedPagesGraph({
  dimensions = defaultDimensions,
  connectedPages,
  headlineLevel,
}: ConnectedPagesGraphProperties) {
  const maxPagesPerRow = 8;

  const { t } = useSonTranslation('ConnectedPagesGraph');

  const location = useLocation();
  const navigate = useNavigate();

  const [graphState, setGraphState] = useState<GraphState>(() => getGraphStateFromUrl(location.search, connectedPages));

  const navigateToGraphState = useCallback(
    (state: GraphState) => {
      const { pageId } = state.currentPage;
      if (isNullOrUndefined(pageId)) {
        return;
      }
      const search = createSearchParams({
        mode: 'graph',
        currentPage: pageId,
        currentSet: state.currentSet.toString(),
      });
      navigate({ search: search.toString() }, { state: { refetch: 'ignoreQuery' } });
    },
    [navigate],
  );

  const { connectedPagesGraphContainer, containerDimensions, radiusOfNodeSphere, setRadiusOfNodeSphere } =
    useConnectedPagesGraphResizeHandler(0);

  /**
   * Keep state in sync with url as long as we stay on the same mode
   * location default is when we initally open the browser with an open graph
   */
  useEffect(() => {
    if (location.search.includes('mode=graph') || location.key === 'default') {
      setGraphState(getGraphStateFromUrl(location.search, connectedPages));
    }
  }, [location.search, connectedPages, location.key]);

  const onClickNode = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, url: string) => {
      event.preventDefault();
      const currentPage = getPageByUrl(url, connectedPages[0]) || connectedPages[0];
      navigateToGraphState({ currentPage, currentSet: 0 });
    },
    [connectedPages, navigateToGraphState],
  );

  const handlePaginationEvent = useCallback(
    (event: PaginationDirection) => {
      if (event === PaginationDirection.NEXT) {
        navigateToGraphState({ ...graphState, currentSet: graphState.currentSet + 1 });
      } else {
        navigateToGraphState({ ...graphState, currentSet: graphState.currentSet - 1 });
      }
    },
    [graphState, navigateToGraphState],
  );

  const isMounted = !!connectedPagesGraphContainer.current;

  return (
    <div className="connected-pages-graph">
      {!isMounted && (
        <div className="connected-pages-graph__loading">
          <LoadingSpinner />
        </div>
      )}
      {isMounted && graphState.currentPage.parentPage && (
        <ConnectedPagesGraphBreadCrumb
          page={
            getPageByUrl(graphState.currentPage.parentPage.url, connectedPages[0]) || graphState.currentPage.parentPage
          }
          headlineLevel={headlineLevel}
          dimension={dimensions}
          isClickable={graphState.currentPage !== connectedPages[0]}
          onClickNode={onClickNode}
        />
      )}
      <ConnectedPagesGraphContainer
        connectedPagesGraphContainer={connectedPagesGraphContainer}
        containerDimensions={containerDimensions}
        current={graphState.currentPage}
        currentSet={graphState.currentSet}
        headline={t('containerHeadline')}
        headlineLevel={headlineLevel}
        maxPagesPerRow={maxPagesPerRow}
        parentPageWidth={dimensions.parentPage.width}
        radiusOfNodeSphere={radiusOfNodeSphere}
        activeCustomActionButton={graphState.activeCustomActionButton}
      >
        {isMounted && (
          <ConnectedPagesGraphCurrentItem
            current={graphState.currentPage}
            currentSet={graphState.currentSet}
            containerDimensions={containerDimensions}
            dimensions={dimensions}
            maxPagesPerRow={maxPagesPerRow}
            radiusOfNodeSphere={radiusOfNodeSphere}
            setRadiusOfNodeSphere={setRadiusOfNodeSphere}
            connectedPages={connectedPages}
            onNextClick={() => handlePaginationEvent(PaginationDirection.NEXT)}
            onPreviousClick={() => handlePaginationEvent(PaginationDirection.PREVIOUS)}
            onCustomButtonClick={(targetId?: string) => {
              setGraphState({
                ...graphState,
                activeCustomActionButton: targetId,
              });
            }}
            onClickNode={onClickNode}
            activeCustomActionButton={graphState.activeCustomActionButton}
          />
        )}
      </ConnectedPagesGraphContainer>
    </div>
  );
}
